import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import UploadIcon from "@mui/icons-material/CloudUploadOutlined";
import { JobUploadDialog } from "@/components/jobs/job-upload-dialog";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { MonoText } from "@/components/display/mono";
import { LoadingState, ErrorState } from "@/components/feedback/states";
import { useUndoableDelete } from "@/lib/undoable-delete";
import { type JobRun, useJob, useJobMutations, useJobRuns } from "@/api/jobs";
import { usePipelineNames } from "@/api/pipelines";
import { fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_app/jobs/$jobId/")({
  component: JobDetail,
});

function JobDetail() {
  const { jobId } = Route.useParams();
  const navigate = useNavigate();
  const job = useJob(jobId);
  const runs = useJobRuns(jobId);
  const names = usePipelineNames();
  const m = useJobMutations();
  const del = useUndoableDelete<{ id: string; name: string }>({
    noun: "Job",
    key: (i) => i.id,
    name: (i) => i.name,
    mutate: (i) => m.remove.mutateAsync(i.id),
    invalidate: [["jobs"]],
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  if (job.isLoading) return <LoadingState />;
  if (job.error || !job.data)
    return <ErrorState error={job.error ?? "Not found"} onRetry={() => job.refetch()} />;

  const runColumns: Column<JobRun>[] = [
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    { id: "trigger", header: "Trigger", cell: (r) => r.triggerType ?? "—", nowrap: true },
    {
      id: "matched",
      header: "Matched",
      align: "right",
      cell: (r) => `${r.filesMatched ?? 0}/${r.filesTotal ?? 0}`,
    },
    { id: "ran", header: "Pipelines", align: "right", cell: (r) => fmtNumber(r.pipelinesRun) },
    { id: "failed", header: "Failed", align: "right", cell: (r) => fmtNumber(r.pipelinesFailed) },
    { id: "when", header: "Started", cell: (r) => <RelativeTime value={r.triggeredAt} /> },
  ];

  return (
    <PageContainer maxWidth={860}>
      <PageHeader
        title={job.data.name}
        breadcrumbs={
          <Breadcrumbs items={[{ label: "Jobs", to: "/jobs" }, { label: job.data.name }]} />
        }
        description={job.data.description ?? undefined}
        actions={
          <>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadOpen(true)}
            >
              Upload &amp; run
            </Button>
            <Button
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => {
                del.remove(
                  { id: jobId, name: job.data!.name },
                  { onUndo: () => navigate({ to: "/jobs/$jobId", params: { jobId } }) },
                );
                navigate({ to: "/jobs" });
              }}
            >
              Delete
            </Button>
          </>
        }
      />

      <JobUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} jobId={jobId} />

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Bindings
      </Typography>
      <Card sx={{ p: 2, mb: 3 }}>
        <Stack spacing={1}>
          {(job.data.bindings ?? []).map((b, i) => (
            <Stack key={b.id ?? i} direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
              <Chip size="small" label={(b.orderIdx ?? i) + 1} />
              <MonoText>{b.filePattern ?? "*"}</MonoText>
              <Typography variant="body2" color="text.secondary">
                →
              </Typography>
              <Typography variant="body2">
                {b.pipelineId ? (names[b.pipelineId] ?? b.pipelineId) : "—"}
              </Typography>
              {b.enabled === false ? <Chip size="small" label="disabled" /> : null}
            </Stack>
          ))}
          {(job.data.bindings ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No bindings.
            </Typography>
          ) : null}
        </Stack>
      </Card>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Run history
      </Typography>
      <Card>
        <DataTable
          columns={runColumns}
          data={runs.data}
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={() => runs.refetch()}
          rowKey={(r) => r.id}
          onRowClick={(r) =>
            navigate({ to: "/jobs/$jobId/runs/$runId", params: { jobId, runId: r.id } })
          }
          empty={<span>No runs yet.</span>}
        />
      </Card>
    </PageContainer>
  );
}
